import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CampaignContext from "../../../../contexts/CampaignContext";
import UserContext from "../../../../contexts/UserContext";
import {
  setTransactionFilters,
  setTransactions,
  transactionFiltersState,
  transactionsState,
} from "../../../../redux/entriesSlice";
import restService from "../../../../restService";
import EntriesTable from "../EntriesTable";
import TablePagination from "../TablePagination";
import Filters from "./filters";

export default function Transactions() {
  const dispatch = useDispatch();

  const controllerRef = useRef(null);

  const [campaign] = useContext(CampaignContext);
  const [user] = useContext(UserContext);

  const transactions = useSelector(transactionsState);
  const transactionsFilters = useSelector(transactionFiltersState);

  const dateRange = transactionsFilters?.dateRange;
  const entryStatus = transactionsFilters?.entryStatus;
  const selectedChannel = transactionsFilters?.selectedChannel;
  const rewardType = transactionsFilters?.rewardType;
  const station = transactionsFilters?.station;
  const network = transactionsFilters?.network;
  const amount = transactionsFilters?.amount;
  const code = transactionsFilters?.code;

  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [allEntries, setAllEntries] = useState(0);
  const [msisdn, setMsisdn] = useState("");

  const [loading, setLoading] = useState(false);
  const [transactionResult, setTransactionResult] = useState("");
  const [containStations, setContainStations] = useState(false);
  const [isGettingDataToExport, setIsGettingDataToExport] = useState(false);
  const [entriesToExport, setEntriesToExport] = useState([]);
  const [emailRecipient, setEmailRecipient] = useState(user?.email);

  function updateTransactionFilters(key, data) {
    dispatch(setTransactionFilters({ [key]: data }));
  }

  function resetTransactionFilters() {
    const newTransactionFilters = {
      ...transactionsFilters,
      network: "",
      entryStatus: "",
      code: "",
    };

    dispatch(setTransactionFilters(newTransactionFilters));
  }

  function searchTermIsStatus(searchTerm) {
    if (
      searchTerm.toLowerCase() === "pending" ||
      searchTerm.toLowerCase() === "redeemed" ||
      searchTerm.toLowerCase() === "win ignore" ||
      searchTerm.toLowerCase() === "fail"
    ) {
      return true;
    }

    return false;
  }

  function searchTermIsNetwork(searchTerm) {
    if (
      searchTerm.toLowerCase() === "mtn" ||
      searchTerm.toLowerCase() === "vodafone" ||
      searchTerm.toLowerCase() === "tigo" ||
      searchTerm.toLowerCase() === "airtel" ||
      searchTerm.toLowerCase() === "glo"
    ) {
      return true;
    }

    return false;
  }

  const handleSearch = async (e) => {
    const searchTerm = await e;

    if (searchTerm) {
      if (!isNaN(Number(searchTerm))) {
        setMsisdn(searchTerm);
      }

      if (searchTermIsStatus(searchTerm)) {
        updateTransactionFilters(
          "entryStatus",
          searchTerm.toUpperCase().replaceAll(" ", "_")
        );
        return;
      }

      if (searchTermIsNetwork(searchTerm)) {
        updateTransactionFilters(
          "network",
          searchTerm.toUpperCase() === "VODAFONE"
            ? "OT"
            : searchTerm.toUpperCase() === "MTN"
            ? "MTNGH"
            : searchTerm.toUpperCase() === "TIGO"
            ? "MYBUZZ"
            : searchTerm.toUpperCase() === "AIRTEL"
            ? "ZAIN_RANCARD"
            : searchTerm.toUpperCase() === "GLO"
            ? "GLO_GHANA"
            : ""
        );

        return;
      }

      if (isNaN(Number(searchTerm))) {
        setMsisdn("");
        updateTransactionFilters("code", searchTerm);
        return;
      }
    } else {
      setMsisdn("");
      resetTransactionFilters();
    }
  };

  useEffect(() => {
    fetchFilteredEntries();
  }, [
    campaign,
    page,
    size,
    msisdn,
    transactionsFilters,
    transactionResult,
    selectedChannel,
  ]);

  const fetchFilteredEntries = async () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    setLoading(true);
    controllerRef.current = new AbortController();
    const signal = controllerRef.current?.signal;

    dispatch(setTransactions([]));

    try {
      const end = dateRange[0].endDate
        ? new Date(dateRange[0].endDate).toISOString()
        : "";
      const start = dateRange[0].startDate
        ? new Date(dateRange[0].startDate).toISOString()
        : "";

      const {
        data: { status, data, totalCount, numberOfPages, currentPage, limit },
      } = await restService.Entries.getFilteredEntries({
        campaignId: campaign?.id,
        page,
        limit: size,
        channel: selectedChannel,
        msisdn: msisdn.replace(/ /g, ""),
        rewardType,
        station,
        network,
        amount,
        status: entryStatus,
        createdTo: end,
        createdFrom: start,
        transactionResult,
        code,
        token: user?.accessToken,
        signal,
      });

      dispatch(setTransactions(data));

      setPages(numberOfPages);

      data?.map((entry) => {
        if (entry.station) {
          setContainStations(true);
        }
      });

      setAllEntries(totalCount);
      setLoading(false);
    } catch (error) {
      if (error?.message === "canceled") return;

      setLoading(false);
    }
  };

  const exportEntries = async (recipient) => {
    try {
      const end = dateRange[0].endDate
        ? new Date(dateRange[0].endDate).toISOString()
        : "";
      const start = dateRange[0].startDate
        ? new Date(dateRange[0].startDate).toISOString()
        : "";

      await restService.Entries.exportEntries({
        campaignId: campaign?.id,
        channel: selectedChannel,
        msisdn: msisdn.replace(/ /g, ""),
        rewardType,
        station,
        network,
        amount,
        status: entryStatus,
        startDate: start,
        endDate: end,
        transactionResult,
        code,
        token: user?.accessToken,
        emailRecipient: recipient,
      });
    } catch (error) {
      console.log(error);
      throw new Error("something went wrong");
    }
  };

  function exportEntriesToastPromise(recipient) {
    toast.promise(exportEntries(recipient), {
      pending: "Sending report",
      success: "Report sent!",
      error: "Something went wrong. Please try again later",
    });
  }

  return (
    <>
      <Filters
        handleSearch={handleSearch}
        entriesToExport={entriesToExport}
        exportEntries={exportEntriesToastPromise}
        isGettingDataToExport={isGettingDataToExport}
        msisdn={msisdn}
        setMsisdn={setMsisdn}
        setPage={setPage}
        setTransactionResult={setTransactionResult}
        transactionResult={transactionResult}
      />

      <div className="my-6 align-middle shadow bg-white w-full">
        <div className="w-full  overflow-auto">
          <EntriesTable
            loading={loading}
            campaign={campaign}
            user={user}
            setMsisdn={setMsisdn}
            setPage={setPage}
            containsStation={containStations}
          />
        </div>
        <TablePagination
          page={page}
          pages={pages}
          setPage={setPage}
          size={size}
          setSize={setSize}
          allEntries={allEntries}
        />
      </div>
    </>
  );
}
